import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { filterTokens, focusRing, webTransition, usePrefersReducedMotion, isWeb } from './mapFilterUi';

const ICON_SETS = { Ionicons, MaterialCommunityIcons };

/**
 * REQ-104: Filter / Video (and, in phase B, Radio) buttons on the map's top-left, beside Pins / Hide.
 * Presentational: props in, callbacks out.
 *
 * props: layers (from constants/mapLayers), activeLayerIds, onToggleLayer(layer), filterCount,
 *        onOpenFilter, filterOpen, isDarkMode, isMobile, left, top, filterButtonRef
 */
export default function MapQuickControls({
  layers = [],
  activeLayerIds = [],
  onToggleLayer,
  filterCount = 0,
  onOpenFilter,
  filterOpen = false,
  isDarkMode = false,
  isMobile = false,
  left = 0,
  top = 12,
  filterButtonRef = null,
}) {
  const reduced = usePrefersReducedMotion();
  if (!isWeb) return null;
  const t = filterTokens(isDarkMode);
  const styles = makeStyles(t, isDarkMode, isMobile);
  const idleColor = isDarkMode ? 'rgba(255,255,255,0.72)' : t.pal.mute;

  const filterLabel = filterCount > 0 ? `Filter venues, ${filterCount} on` : 'Filter venues';

  return (
    <View style={[styles.wrap, { left, top }]} pointerEvents="box-none">
      <View style={styles.group}>
        <Pressable
          ref={filterButtonRef}
          onPress={onOpenFilter}
          accessibilityRole="button"
          accessibilityLabel={filterLabel}
          accessibilityState={{ expanded: filterOpen }}
          {...(isWeb ? { 'aria-haspopup': 'dialog', 'aria-expanded': filterOpen, title: 'Filter venues' } : {})}
          style={({ hovered, focused, pressed }) => [
            styles.btn,
            (filterOpen || filterCount > 0) && styles.btnOn,
            hovered && !(filterOpen || filterCount > 0) && styles.btnHover,
            pressed && !reduced && styles.btnPressed,
            webTransition(reduced),
            focusRing(focused, isDarkMode),
          ]}
        >
          <Ionicons name="funnel-outline" size={isMobile ? 16 : 13} color={filterCount > 0 || filterOpen ? '#fffffe' : idleColor} />
          {!isMobile ? (
            <Text style={[styles.label, (filterOpen || filterCount > 0) && styles.labelOn]}>Filter</Text>
          ) : null}
          {filterCount > 0 ? (
            <View style={styles.badge} accessibilityElementsHidden importantForAccessibility="no">
              <Text style={styles.badgeText}>{filterCount > 99 ? '99+' : filterCount}</Text>
            </View>
          ) : null}
        </Pressable>

        {layers.map((layer) => {
          const on = activeLayerIds.includes(layer.id);
          const Icon = ICON_SETS[layer.icon?.set] || Ionicons;
          return (
            <Pressable
              key={layer.id}
              onPress={() => onToggleLayer?.(layer)}
              accessibilityRole="switch"
              accessibilityLabel={layer.hint || layer.label}
              accessibilityState={{ checked: on }}
              {...(isWeb ? { 'aria-checked': on, title: layer.hint || layer.label } : {})}
              style={({ hovered, focused, pressed }) => [
                styles.btn,
                on && styles.btnOn,
                hovered && !on && styles.btnHover,
                pressed && !reduced && styles.btnPressed,
                webTransition(reduced),
                focusRing(focused, isDarkMode),
              ]}
            >
              <Icon name={layer.icon?.name} size={isMobile ? 18 : 15} color={on ? '#fffffe' : idleColor} />
              {!isMobile ? <Text style={[styles.label, on && styles.labelOn]}>{layer.label}</Text> : null}
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

function makeStyles(t, isDarkMode, isMobile) {
  return StyleSheet.create({
    wrap: {
      position: 'absolute',
      zIndex: 8, // same layer as Pins / Hide: under open rail panels, above the map canvas
      flexDirection: 'row',
    },
    group: {
      flexDirection: 'row',
      gap: 3,
      padding: 3,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: t.pal.line,
      backgroundColor: t.surface,
      ...(isWeb ? { backdropFilter: 'blur(6px)' } : {}),
    },
    btn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 5,
      borderRadius: 6,
      ...(isMobile
        ? { width: 36, height: 36 }
        : { paddingVertical: 6, paddingHorizontal: 10 }),
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    btnHover: { backgroundColor: t.hover },
    btnOn: { backgroundColor: t.fill },
    btnPressed: { transform: [{ scale: 0.96 }] },
    label: {
      fontSize: 11.5,
      fontWeight: '600',
      fontFamily: t.sans,
      color: isDarkMode ? 'rgba(255,255,255,0.72)' : t.pal.mute,
    },
    labelOn: { color: '#fffffe' },
    badge: {
      minWidth: 16,
      height: 16,
      paddingHorizontal: 4,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#fffffe',
      ...(isMobile ? { position: 'absolute', top: 1, right: 1, minWidth: 14, height: 14 } : {}),
    },
    badgeText: {
      fontSize: isMobile ? 9 : 10,
      fontWeight: '700',
      fontFamily: t.mono,
      color: '#1F7563',
    },
  });
}
